import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  PLATFORM_ID,
  ViewChild,
  computed,
  input,
  signal,
  inject,
} from '@angular/core';
import Player from '@vimeo/player';
import type { VimeoEmbedParameters } from '@vimeo/player/types/formats';
import { CommonModule as SSCommonModule, fadeInOut } from 'common';
import { BehaviorSubject } from 'rxjs';
import { HeroImage, HeroVideo } from 'www-cms';
import { ImageWithLoaderComponent } from '../components/image-with-loader/image-with-loader.component';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { PlayButtonOverlayComponent } from '../components/play-button-overlay/play-button-overlay.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { PopUpVideoComponent } from 'building-blocks/pop-up-video/pop-up-video.component';
import { CookieConsentService } from 'cookie-consent/lib/services/cookie-consent.service';
import { DirectusDirective } from 'directus';

@Component({
  selector: 'ss-www-hero-media',
  imports: [
    CommonModule,
    SSCommonModule,
    ImageWithLoaderComponent,
    PlayButtonOverlayComponent,
    PopUpVideoComponent,
    DirectusDirective,
  ],
  templateUrl: './hero-media.component.html',
  styleUrls: ['./hero-media.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInOut,
    trigger('justFadeOut', [
      transition(':leave', [animate('300ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class HeroMediaComponent
  extends BaseBuildingBlockComponent
  implements AfterViewInit
{
  private platformId = inject<Object>(PLATFORM_ID);
  private readonly consentService = inject(CookieConsentService);

  @Input() heroImageData?: HeroImage;
  heroVideoData = input<HeroVideo>();
  private srcSetSizes = [295, 640, 960, 1280];
  sizes = this.srcSetSizes
    .map((size, index) => {
      if (index === this.srcSetSizes.length - 1) {
        return `${size}px`;
      }
      return `(max-width: ${Math.round((size + this.srcSetSizes[index + 1]) / 2)}px) ${size}px`;
    })
    .join(', ');
  thumbnailSrcSet = computed(() => {
    if (!this.heroVideoData()) return '';
    return this.srcSetSizes
      .map((size) => {
        return `${this.heroVideoData()!.videoThumbnail.slice(0, this.heroVideoData()!.videoThumbnail.lastIndexOf('_'))}_${size} ${size}w`;
      })
      .join(', ');
  });
  videoStartedPlaying = signal(false);
  @Input() priority = false;

  @ViewChild('heroVideo') heroVideo?: ElementRef<HTMLDivElement>;

  imageLoaded$ = new BehaviorSubject(true);
  private player?: Player;

  playClicked$ = new BehaviorSubject(false);

  ngAfterViewInit(): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      !this.heroVideoData ||
      !this.heroVideo ||
      !this.heroVideoData()?.autoPlay
    ) {
      return;
    }

    const config: VimeoEmbedParameters = {
      id: parseInt(this.heroVideoData()!.video),
      controls: false,
      responsive: true,
      background: this.heroVideoData()!.autoPlay,
      dnt: this.consentService.consent().performance,
    };

    this.player = new Player(this.heroVideo.nativeElement, config);
    this.player.on('play', () => {
      this.videoStartedPlaying.set(true);
    });
  }

  override getBgClass(): LastItemBgClass {
    return 'bg-ss-www-background';
  }

  async toggleVideo() {
    if (await this.player?.getPaused()) {
      await this.player?.play();
    } else {
      await this.player?.pause();
    }
  }
}
